import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getToken, decodeToken } from "../utils/authUtils";
import { handleLogout } from "../utils/logoutUtils";
import { useTranslation } from "react-i18next";

const AuthGuard = ({ children, setSnackbarMessage, setOpenSnackbar, setOpenBackdrop }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const [tokenCheckTrigger, setTokenCheckTrigger] = useState(0);

	const pathSegments = location.pathname.split("/");
	const pathLang = pathSegments[1] || "";
	const lang = pathLang;

	// 定期檢查 token 變化
	useEffect(() => {
		const interval = setInterval(() => {
			setTokenCheckTrigger(prev => prev + 1);
		}, 5000); // 每5秒檢查一次

		return () => clearInterval(interval);
	}, []);

	// 定義公開路由（不需要驗證的路由）
	const isPublicRoute = useMemo(() => {
		const base = `/${pathLang}`;
		const p = location.pathname;

		return (
			p === base ||
			p === `${base}/login-page` ||
			p === `${base}/landing-page` ||
			p === `${base}/product-catalog` ||
			p === `${base}/user-manual` ||
			p.startsWith(`${base}/reset-password-page`) ||
			p === `${base}/reset-password-success-page`
		);
	}, [location.pathname, pathLang]);

	// 同步檢查認證狀態
	const isAuthenticated = useMemo(() => {
		// 如果是公開路由，直接允許訪問
		if (isPublicRoute) {
			return true;
		}

		// 檢查token是否存在
		const token = getToken();
		if (!token) {
			// 立即處理logout，使用微任務確保不阻塞渲染檢查
			Promise.resolve().then(() => {
				handleLogout({
					t,
					lang,
					setSnackbarMessage,
					setOpenSnackbar,
					setOpenBackdrop,
					navigate,
					logoutMessage: t("mapPage.tokenExpired") || "Token已過期，請重新登入",
				});
			});
			return false;
		}

		// 檢查token是否過期
		try {
			const decodedToken = decodeToken();
			if (!decodedToken) {
				throw new Error("Invalid token");
			}

			const currentTime = Date.now() / 1000;
			if (decodedToken.exp < currentTime) {
				// 立即處理logout，使用微任務確保不阻塞渲染檢查
				Promise.resolve().then(() => {
					handleLogout({
						t,
						lang,
						setSnackbarMessage,
						setOpenSnackbar,
						setOpenBackdrop,
						navigate,
						logoutMessage: t("mapPage.tokenExpired") || "Token已過期，請重新登入",
					});
				});
				return false;
			}

			// Token有效，允許訪問
			return true;
		} catch (error) {
			console.error("Token validation error:", error);
			// 立即處理logout，使用微任務確保不阻塞渲染檢查
			Promise.resolve().then(() => {
				handleLogout({
					t,
					lang,
					setSnackbarMessage,
					setOpenSnackbar,
					setOpenBackdrop,
					navigate,
					logoutMessage: t("mapPage.invalidToken") || "Token無效，請重新登入",
				});
			});
			return false;
		}
	}, [location.pathname, isPublicRoute, t, lang, navigate, setSnackbarMessage, setOpenSnackbar, setOpenBackdrop, tokenCheckTrigger]);

	// 如果驗證失敗且不是公開路由，不渲染任何內容
	if (!isAuthenticated && !isPublicRoute) {
		return null;
	}

	// 驗證通過或是公開路由，渲染子組件
	return children;
};

export default AuthGuard;